import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import MDButton from "components/MDButton";
import CanvasContainer from "../../canvas/CanvasContainer.tsx";
import { basicURL } from "../../apiManager/APIConfig";
import {
  GetSharedPresentationCall,
  GetSharedPresentationSlidesCall,
  RequestPresentationCodeCall,
  VerifyPresentationCodeCall,
} from "../../redux/api/presentationShare.api";

const STAGE_W = 1280;
const STAGE_H = 720;

function sessionKey(token) {
  return `ml-pres-view:${token}`;
}

function readSession(token) {
  try {
    const raw = sessionStorage.getItem(sessionKey(token));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.viewerToken) return null;
    if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() < Date.now()) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function modelUrlFromSlide(slide) {
  const files = slide?.Files ?? slide?.files ?? [];
  const first = Array.isArray(files) ? files[0] : null;
  let url = first?.url ?? first?.path ?? null;
  if (typeof url !== "string" || !url.trim()) return null;
  const trimmed = url.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  const base = String(basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  return trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
}

function slideCanvas(slide) {
  let data = slide?.content?.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (_) {
      data = [];
    }
  }
  if (data && !Array.isArray(data) && typeof data === "object") {
    if (Array.isArray(data.elements)) data = data.elements;
    else if (Array.isArray(data.data)) data = data.data;
    else data = [];
  }
  return Array.isArray(data) ? data : [];
}

export default function PublicPresentationPage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const theme = useTheme();
  const stageRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [session, setSession] = useState(() => readSession(token));
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [slides, setSlides] = useState(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setMeta(null);
    setSlides(null);
    GetSharedPresentationCall(token)
      .then((res) => {
        if (!cancelled) setMeta(res.data || { state: "unavailable" });
      })
      .catch(() => {
        if (!cancelled) setMeta({ state: "unavailable" });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const canLoadSlides = meta?.state === "public" || (meta?.state === "verification" && session?.viewerToken);

  useEffect(() => {
    if (!canLoadSlides) return undefined;
    let cancelled = false;
    setSlides(null);
    GetSharedPresentationSlidesCall(token, session?.viewerToken).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        sessionStorage.removeItem(sessionKey(token));
        setSession(null);
        setSlides([]);
        return;
      }
      setSlides(Array.isArray(res.data) ? res.data : []);
    });
    return () => {
      cancelled = true;
    };
  }, [canLoadSlides, token, session]);

  const slide = slides?.[index] || null;
  const canvasData = useMemo(() => slideCanvas(slide), [slide]);
  const type = slide?.parentTitle?.type || slide?.title_data?.type || slide?.type || "";
  const modelUrl = type === "three_d_model" ? modelUrlFromSlide(slide) : null;

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage || !slide) return undefined;
    const layout = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight - 72;
      if (!vw || vh <= 0) return;
      const scale = Math.min(vw / STAGE_W, vh / STAGE_H);
      const x = (vw - STAGE_W * scale) / 2;
      const y = (vh - STAGE_H * scale) / 2;
      stage.style.width = `${STAGE_W}px`;
      stage.style.height = `${STAGE_H}px`;
      stage.style.transformOrigin = "top left";
      stage.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    };
    layout();
    window.addEventListener("resize", layout);
    return () => window.removeEventListener("resize", layout);
  }, [slide]);

  useEffect(() => {
    const onKey = (event) => {
      if (!slides?.length) return;
      if (event.key === "ArrowRight") setIndex((value) => Math.min(slides.length - 1, value + 1));
      if (event.key === "ArrowLeft") setIndex((value) => Math.max(0, value - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides]);

  const requestCode = async () => {
    setBusy(true);
    setNotice("");
    await RequestPresentationCodeCall(token, email.trim());
    setBusy(false);
    setStep("code");
    setNotice(t("pages.presentationView.codeSent"));
  };

  const verifyCode = async () => {
    setBusy(true);
    setNotice("");
    const res = await VerifyPresentationCodeCall(token, email.trim(), code.trim());
    setBusy(false);
    if (!res.ok || !res.data?.viewerToken) {
      setNotice(res.message || t("pages.presentationView.codeInvalid"));
      return;
    }
    const next = { viewerToken: res.data.viewerToken, expiresAt: res.data.expiresAt, email: email.trim() };
    sessionStorage.setItem(sessionKey(token), JSON.stringify(next));
    setSession(next);
  };

  if (!meta) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (meta.state === "unavailable") {
    return (
      <CenterCard
        title={t("pages.presentationView.unavailableTitle")}
        body={t("pages.presentationView.unavailableBody")}
      />
    );
  }

  if (meta.state === "verification" && !session) {
    return (
      <CenterCard title={meta.title || t("pages.presentationView.accessTitle")} body={t("pages.presentationView.accessBody")}>
        {step === "email" ? (
          <TextField
            fullWidth
            type="email"
            value={email}
            autoFocus
            placeholder={t("pages.presentationView.emailPlaceholder")}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") requestCode();
            }}
            sx={{ mt: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
          />
        ) : (
          <TextField
            fullWidth
            value={code}
            autoFocus
            inputProps={{ inputMode: "numeric", maxLength: 6, style: { letterSpacing: 8, fontSize: 22, textAlign: "center" } }}
            placeholder="000000"
            onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(event) => {
              if (event.key === "Enter") verifyCode();
            }}
            sx={{ mt: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
          />
        )}
        {notice ? (
          <Typography sx={{ mt: 1.5, fontSize: 13, color: "#64748b", textAlign: "center" }}>{notice}</Typography>
        ) : null}
        <MDButton
          fullWidth
          variant="contained"
          color="info"
          disabled={busy || (step === "email" ? !email.trim() : code.length < 6)}
          onClick={step === "email" ? requestCode : verifyCode}
          sx={{ mt: 2, textTransform: "none", borderRadius: 999, boxShadow: "none" }}
        >
          {step === "email" ? t("pages.presentationView.continue") : t("pages.presentationView.open")}
        </MDButton>
      </CenterCard>
    );
  }

  if (!slides) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: theme.palette.background.default }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "fixed", inset: 0, bgcolor: "#0f172a", overflow: "hidden" }}>
      <Box ref={stageRef} sx={{ position: "absolute", left: 0, top: 0, bgcolor: "#fff", overflow: "hidden" }}>
        {modelUrl ? (
          <iframe
            title={meta.title || "3D"}
            src={`${window.location.origin}/model-viewer.html?src=${encodeURIComponent(modelUrl)}&theme=light`}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        ) : (
          <CanvasContainer
            key={slide?.id || index}
            initialData={canvasData}
            isPlayMode
            showAddButtons={false}
            stageWidth={STAGE_W}
            stageHeight={STAGE_H}
            viewMode="web"
            clipStage
          />
        )}
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 16,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            pointerEvents: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            py: 0.5,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,0.92)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          }}
        >
          <IconButton size="small" disabled={index <= 0} onClick={() => setIndex((value) => Math.max(0, value - 1))}>
            ‹
          </IconButton>
          <Typography sx={{ fontSize: 13, fontWeight: 650, color: "#0f172a", minWidth: 72, textAlign: "center" }}>
            {slides.length ? `${index + 1} / ${slides.length}` : "—"}
          </Typography>
          <IconButton
            size="small"
            disabled={index >= slides.length - 1}
            onClick={() => setIndex((value) => Math.min(slides.length - 1, value + 1))}
          >
            ›
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

function CenterCard({ title, body, children }) {
  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#f8fafc", px: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 420, bgcolor: "#fff", borderRadius: 4, px: 3.5, py: 4, boxShadow: "0 20px 50px rgba(15,23,42,0.08)" }}>
        <Typography sx={{ fontSize: 26, fontWeight: 650, letterSpacing: "-0.03em", color: "#0f172a" }}>{title}</Typography>
        <Typography sx={{ mt: 1, color: "#64748b", fontSize: 14.5, lineHeight: 1.5 }}>{body}</Typography>
        {children}
      </Box>
    </Box>
  );
}
